import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { styled } from "~/application/theme";
import { Card } from "~/components/Card";
import { EmptyState } from "~/components/EmptyState";
import { Skeleton } from "~/components/Skeleton";
import { PolicyComplianceResponse } from "../types";
import { asCount } from "../utils";
import { ChartTooltip } from "./ChartTooltip";

export interface PolicyTrendsProps {
  data?: PolicyComplianceResponse;
  isFetching: boolean;
}

const ChartContainer = styled("div", {
  width: "100%",
  height: "320px",
});

const StyledResponsiveContainer = styled(ResponsiveContainer, {
  "& .recharts-cartesian-grid-horizontal, & .recharts-cartesian-grid-vertical": {
    stroke: "$neutral-200",
  },
  "& .recharts-cartesian-axis-tick-value": {
    fill: "$neutrals-dark",
    fontSize: "$xs",
  },
});

// `stroke` is a raw SVG attribute on recharts elements and cannot resolve
// Stitches `$token` references — use literal hex values.
const INSIDE_COLOR = "#00a878";
const OUTSIDE_COLOR = "#e74c3c";

interface PolicyTrendPoint {
  yearMonth: string;
  insideCount: number;
  outsideCount: number;
}

const buildMonthlySeries = (data: PolicyComplianceResponse): PolicyTrendPoint[] => {
  const byMonth = new Map<string, PolicyTrendPoint>();

  for (const item of data.compliance) {
    const current = byMonth.get(item.yearMonth) ?? {
      yearMonth: item.yearMonth,
      insideCount: 0,
      outsideCount: 0,
    };
    current.insideCount += item.notViolatedCount ?? 0;
    current.outsideCount += item.violatedCount ?? 0;
    byMonth.set(item.yearMonth, current);
  }

  return Array.from(byMonth.values()).sort((a, b) => a.yearMonth.localeCompare(b.yearMonth));
};

export const PolicyTrends = ({ data, isFetching }: PolicyTrendsProps) => {
  if (isFetching || !data) {
    return (
      <Card css={{ p: "$6" }}>
        <Skeleton variant="text-6" css={{ width: "40%" }} />
        <Skeleton css={{ width: "100%", height: "320px", mt: "$4" }} />
      </Card>
    );
  }

  if (data.compliance.length === 0) {
    return (
      <EmptyState>Nenhum dado de conformidade de políticas disponível para o período.</EmptyState>
    );
  }

  const series = buildMonthlySeries(data);

  return (
    <Card css={{ p: "$6" }}>
      <ChartContainer>
        <StyledResponsiveContainer width="100%" height="100%">
          <LineChart data={series} margin={{ top: 8, right: 16, left: 8, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="yearMonth" tickLine={false} axisLine={false} tickMargin={8} />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickFormatter={(value: number) => asCount(value)}
            />
            <Tooltip
              cursor={{ stroke: "#E4E4E7" }}
              content={<ChartTooltip valueFormatter={asCount} />}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="insideCount"
              name="Dentro da Política"
              stroke={INSIDE_COLOR}
              strokeWidth={2}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="outsideCount"
              name="Fora da Política"
              stroke={OUTSIDE_COLOR}
              strokeWidth={2}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </StyledResponsiveContainer>
      </ChartContainer>
    </Card>
  );
};

PolicyTrends.displayName = "PolicyTrends";
